/**
 * Tarayıcıdaki hataların sunucu günlüğüne (Seq) gönderilmesi.
 *
 * Bir ekran çöktüğünde ya da beklenmeyen bir yanıt geldiğinde geriye yalnızca
 * kullanıcının konsolu kalıyordu; destek "hata aldım" dışında bir şey göremiyordu.
 * Buradan giden olaylar DAL'ın `api/clientlog` ucuna yazılır ve sunucu kayıtlarıyla
 * aynı hata kimliği altında yan yana durur.
 *
 * Tasarım kararları:
 * - Gönderim TOPLU ve gecikmeli: bir hata çoğu zaman peşinden onlarcasını getiriyor
 *   (render döngüsü), her biri için ayrı istek atmak ağı ve sunucuyu meşgul ederdi.
 * - Aynı mesaj kısa aralıkla tekrar ederse bir kez gider: sonsuz döngüye giren bir
 *   bileşen günlüğü doldurmasın.
 * - HİÇBİR koşulda hata fırlatmaz ve uygulamayı bekletmez; gönderim başarısızsa olay
 *   sessizce düşer. Loglama, uygulamanın çalışmasının önüne geçmez.
 * - Sayfa kapanırken bekleyenler `sendBeacon` ile gider (fetch iptal edilirdi).
 */
import { runtimeConfig } from '@/app/config/runtimeConfig';
import { fingerprintOf } from './fingerprint';

export type ClientLogLevel = 'fatal' | 'error' | 'warning' | 'information';
export type ClientLogSource = 'boundary' | 'window' | 'promise' | 'api' | 'manual';

export interface ClientLogEvent {
  level?: ClientLogLevel;
  message: string;
  stack?: string | null;
  /** Sunucudan dönen hata kimliği — istemci ve sunucu kaydını birleştirir */
  errorId?: string | null;
  screen?: string | null;
  source?: ClientLogSource;
  url?: string;
  /** Hata tipi (Error.name) — parmak izinin ayrıştırıcı parçası */
  kind?: string;
  /** Hesaplanmış parmak izi; verilmezse buradan üretilir */
  fingerprint?: string;
  /** Hatadan önceki son adımlar (otomatik eklenir) */
  breadcrumbs?: string[];
}

/** Kullanıcının hatadan önce ne yaptığı — halka tampon. */
export interface Breadcrumb {
  /** ekran | işlem | rapor | oturum | ağ */
  category: string;
  message: string;
  at: string;
}

const FLUSH_DELAY_MS = 1500;
const MAX_QUEUE = 20;
/** Aynı mesajın tekrar gönderilmeden önce beklemesi gereken süre. */
const DEDUPE_MS = 10_000;
/**
 * Hatanın ARKASINDAKİ adımlar — "ne yaparken patladı" sorusunun yanıtı.
 *
 * Yığın izi hatanın nerede olduğunu söylüyor, neden oluştuğunu değil. Kullanıcının son
 * birkaç hareketi (hangi ekranı açtı, hangi işlemi çalıştırdı) bir hatayı tekrar
 * üretmenin en kısa yolu. Tampon küçük: hafıza değil, son bağlam tutuluyor.
 */
const MAX_BREADCRUMBS = 20;

interface Context {
  userCode?: string | null;
  version?: string | null;
  /** Açık ekranın adı — çağıran kabuk günceller */
  screen?: string | null;
}

const queue: ClientLogEvent[] = [];
const breadcrumbs: Breadcrumb[] = [];
const lastSeen = new Map<string, number>();
let timer: ReturnType<typeof setTimeout> | null = null;
let context: Context = {};

/** Kabuk oturum açtıkça/ekran değiştikçe bağlamı tazeler. */
export function setClientLogContext(next: Context) {
  context = { ...context, ...next };
}

/**
 * Kullanıcı adımını tampona yazar. Hata olmadığı sürece hiçbir yere gitmez —
 * yalnızca bir hata gönderildiğinde son adımlar onunla birlikte taşınır.
 */
export function addBreadcrumb(category: string, message: string) {
  try {
    breadcrumbs.push({ category, message: String(message).slice(0, 160), at: new Date().toISOString() });
    if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.splice(0, breadcrumbs.length - MAX_BREADCRUMBS);
  } catch {
    /* tampon hiçbir koşulda akışı bozmamalı */
  }
}

/** Testler ve tanılama için: tamponun kopyası. */
export function currentBreadcrumbs(): Breadcrumb[] {
  return breadcrumbs.slice();
}

function endpoint(): string | null {
  try {
    const base = runtimeConfig().dalUrl;
    return base ? `${base.replace(/\/$/, '')}/api/clientlog` : null;
  } catch {
    return null;
  }
}

function payload() {
  return JSON.stringify({
    app: 'Octo.Web.React',
    version: context.version ?? null,
    userAgent: typeof navigator === 'undefined' ? null : navigator.userAgent,
    userCode: context.userCode ?? null,
    events: queue.splice(0, MAX_QUEUE),
  });
}

function flush(useBeacon = false) {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0) return;
  const url = endpoint();
  if (!url) {
    queue.length = 0;
    return;
  }
  const body = payload();
  try {
    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      // Kimlik göndermeye çalışılmaz: uç anonim ve hata çoğu zaman oturum düşerken geliyor.
      keepalive: true,
    }).catch(() => {
      /* günlük gönderilemedi — uygulamayı ilgilendirmez */
    });
  } catch {
    /* ağ yok / engellendi */
  }
}

/** Olayı kuyruğa alır; gönderim toplu yapılır. Asla hata fırlatmaz. */
export function logClientEvent(event: ClientLogEvent) {
  try {
    const key = `${event.source ?? 'manual'}|${event.message}`.slice(0, 300);
    const now = Date.now();
    const seen = lastSeen.get(key);
    if (seen && now - seen < DEDUPE_MS) return;
    lastSeen.set(key, now);

    queue.push({
      level: event.level ?? 'error',
      message: event.message,
      stack: event.stack ?? null,
      errorId: event.errorId ?? null,
      screen: event.screen ?? context.screen ?? null,
      source: event.source ?? 'manual',
      url: event.url ?? (typeof location === 'undefined' ? undefined : location.href),
      kind: event.kind ?? null,
      // Parmak izi İSTEMCİDE hesaplanıyor: yığının tam metni yalnızca burada var.
      fingerprint:
        event.fingerprint ??
        fingerprintOf({ kind: event.kind ?? event.source ?? 'Error', message: event.message, stack: event.stack }),
      breadcrumbs: breadcrumbs.map((b) => `${b.at.slice(11, 19)} ${b.category}: ${b.message}`),
    } as ClientLogEvent);
    if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);

    if (!timer) timer = setTimeout(() => flush(), FLUSH_DELAY_MS);
  } catch {
    /* loglama hiçbir koşulda akışı bozmamalı */
  }
}

/** Yakalanmamış hata ve promise reddi dinleyicilerini kurar (bir kez çağrılır). */
export function installGlobalErrorLogging() {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (e: ErrorEvent) => {
    // Kaynak yükleme hataları (img/script) `message` taşımaz; gürültü yapmasınlar.
    if (!e.message) return;
    logClientEvent({
      source: 'window',
      level: 'error',
      kind: e.error instanceof Error ? e.error.name : 'Error',
      message: e.message,
      stack: e.error instanceof Error ? e.error.stack : `${e.filename}:${e.lineno}:${e.colno}`,
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    const message = reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : 'Yakalanmamış promise reddi';
    logClientEvent({
      source: 'promise',
      level: 'error',
      kind: reason instanceof Error ? reason.name : 'UnhandledRejection',
      message,
      stack: reason instanceof Error ? reason.stack : null,
    });
  });

  // Sekme kapanırken bekleyenler kaybolmasın.
  window.addEventListener('pagehide', () => flush(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
}

/** Testler için: kuyruğu ve tekrar süzgecini sıfırlar. */
export function __resetClientLog() {
  queue.length = 0;
  breadcrumbs.length = 0;
  lastSeen.clear();
  if (timer) clearTimeout(timer);
  timer = null;
  context = {};
}
